<template>
  <div class="saved-list">
    <article v-for="n in notes" :key="n.uid" class="saved-list__item">
      <div class="saved-list__head">
        <span v-if="n.label" class="saved-list__label">{{ n.label }}</span>
        <span v-else class="saved-list__label saved-list__label--none"
          >Geen label</span
        >
        <span v-if="n.createdAt" class="saved-list__time">{{
          formatDate(n.createdAt)
        }}</span>
      </div>

      <p
        class="saved-list__text"
        :class="{ 'saved-list__text--clamped': !isExpanded(n) && isLong(n) }"
      >
        {{ n.text }}
      </p>
      <button
        v-if="isLong(n)"
        type="button"
        class="saved-list__more"
        @click="toggle(n)"
      >
        {{ isExpanded(n) ? "Minder" : "Meer" }}
      </button>

      <p v-if="n.note" class="saved-list__note">{{ n.note }}</p>

      <div class="saved-list__actions">
        <button
          type="button"
          class="saved-list__action"
          :title="copiedKey === n.uid ? 'Gekopieerd!' : 'Kopiëren'"
          @click="copy(n)"
        >
          <Icon name="copy" :size="13" />
          <span>{{ copiedKey === n.uid ? "Gekopieerd" : "Kopiëren" }}</span>
        </button>
        <button
          type="button"
          class="saved-list__action"
          title="Terug in chat brengen"
          @click="$emit('bring-to-chat', n)"
        >
          <Icon name="arrow-right" :size="13" />
          <span>In chat</span>
        </button>
        <button
          type="button"
          class="saved-list__action"
          title="Bewerken"
          @click="$emit('edit', n)"
        >
          <Icon name="pencil" :size="13" />
          <span>Bewerken</span>
        </button>
        <button
          v-if="!$store.getters.isGuest"
          type="button"
          class="saved-list__action saved-list__action--danger"
          title="Verwijderen"
          @click="confirmDelete(n)"
        >
          <Icon name="trash" :size="13" />
        </button>
      </div>
    </article>

    <div v-if="!notes.length" class="saved-list__empty">
      <slot name="empty">Nog niets bewaard.</slot>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/alice/Icon.vue";
import { copyFeedbackMixin } from "@/utils/clipboard";

const CLAMP_LENGTH = 280;

export default {
  name: "SavedNotesList",
  components: { Icon },
  mixins: [copyFeedbackMixin],
  props: {
    notes: { type: Array, default: () => [] },
  },
  emits: ["bring-to-chat", "edit", "delete"],
  data() {
    return {
      expanded: {},
    };
  },
  methods: {
    isLong(n) {
      return (n.text || "").length > CLAMP_LENGTH;
    },
    isExpanded(n) {
      return !!this.expanded[n.uid];
    },
    toggle(n) {
      this.expanded = { ...this.expanded, [n.uid]: !this.expanded[n.uid] };
    },
    copy(n) {
      this.copyWithFeedback(n.uid, n.text || "");
    },
    confirmDelete(n) {
      const label = n.label || "deze notitie";
      if (!confirm(`Notitie "${label}" verwijderen?`)) return;
      this.$emit("delete", n);
    },
    formatDate(ts) {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.saved-list {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__item {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 12px 14px;
    background: #fff;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.7);

    &--none {
      opacity: 0.5;
      font-weight: 400;
    }
  }

  &__time {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    white-space: nowrap;
  }

  &__text {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;

    &--clamped {
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__more {
    margin-top: 4px;
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-theme-primary, #3b3bff);
    cursor: pointer;
  }

  &__note {
    margin: 8px 0 0;
    font-size: 12px;
    font-style: italic;
    color: rgba(0, 0, 0, 0.6);
    border-left: 2px solid rgba(0, 0, 0, 0.15);
    padding-left: 8px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    padding: 4px 8px;
    background: #fff;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.75);
    transition: background 0.12s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &--danger {
      color: #c0392b;
      border-color: rgba(192, 57, 43, 0.3);

      &:hover {
        background: rgba(192, 57, 43, 0.08);
      }
    }
  }

  &__empty {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
    padding: 16px 4px;
    text-align: center;
  }
}
</style>
